package features.booking

import androidx.compose.foundation.gestures.animateScrollBy
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ChevronLeft
import androidx.compose.material.icons.filled.ChevronRight
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.launch

/**
 * Carrusel horizontal con flechas de avance/retroceso.
 *
 * El scroll lateral con rueda solo funciona en trackpad/Magic Mouse; con un mouse normal
 * no hay eje horizontal y el contenido queda inalcanzable. Las flechas dan una salida
 * clara con cualquier dispositivo.
 *
 * Solo aparecen cuando hay algo que mostrar en esa dirección: si todo cabe en pantalla,
 * el carrusel se ve igual que antes.
 */
@Composable
fun CarruselHorizontal(
    modifier: Modifier = Modifier,
    horizontalArrangement: Arrangement.Horizontal = Arrangement.spacedBy(12.dp),
    contentPadding: PaddingValues = PaddingValues(0.dp),
    content: LazyListScope.() -> Unit
) {
    val estado = rememberLazyListState()
    val scope = rememberCoroutineScope()
    val hayIzquierda by remember { derivedStateOf { estado.canScrollBackward } }
    val hayDerecha by remember { derivedStateOf { estado.canScrollForward } }

    // Avanza ~80% del ancho visible: deja a la vista un elemento del bloque anterior
    // para no perder el hilo de dónde estabas.
    fun desplazar(haciaDerecha: Boolean) {
        scope.launch {
            val info = estado.layoutInfo
            val ancho = (info.viewportEndOffset - info.viewportStartOffset).toFloat()
            val salto = if (ancho > 0f) ancho * 0.8f else 300f
            estado.animateScrollBy(if (haciaDerecha) salto else -salto)
        }
    }

    Box(modifier = modifier.fillMaxWidth()) {
        LazyRow(
            state = estado,
            horizontalArrangement = horizontalArrangement,
            contentPadding = contentPadding,
            modifier = Modifier.fillMaxWidth(),
            content = content
        )
        if (hayIzquierda) {
            FlechaCarrusel(
                izquierda = true,
                onClick = { desplazar(false) },
                modifier = Modifier.align(Alignment.CenterStart)
            )
        }
        if (hayDerecha) {
            FlechaCarrusel(
                izquierda = false,
                onClick = { desplazar(true) },
                modifier = Modifier.align(Alignment.CenterEnd)
            )
        }
    }
}

@Composable
private fun FlechaCarrusel(izquierda: Boolean, onClick: () -> Unit, modifier: Modifier = Modifier) {
    Surface(
        onClick = onClick,
        modifier = modifier.size(32.dp).pointerHoverIcon(PointerIcon.Hand),
        shape = CircleShape,
        color = Color.White,
        shadowElevation = 4.dp,
        border = androidx.compose.foundation.BorderStroke(1.dp, Color(0x22000000))
    ) {
        Box(contentAlignment = Alignment.Center) {
            Icon(
                imageVector = if (izquierda) Icons.Filled.ChevronLeft else Icons.Filled.ChevronRight,
                contentDescription = if (izquierda) "Anterior" else "Siguiente",
                tint = SmartVanColors.AzulOscuro,
                modifier = Modifier.size(22.dp)
            )
        }
    }
}
